import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import type { MultiselectOption } from '@rio-cloud/rio-uikit/Multiselect';

import type { RangeValue } from '@/features/brakes/hooks/useBrakesQueryParams';
import RangeInputDropdown from '@/features/common/components/RangeInputDropdown';
import { FILTER_MSG } from '@/features/maintenance-data/filters/constants';
import FilterMultiselect from '@/features/maintenance-data/filters/FilterMultiselect';
import { DueAtChange, MaintenanceDueAtFilter } from '@/features/maintenance-data/filters/MaintenanceDueAtFilter';
import { useMaintenanceComponentOptions } from '@/features/maintenance-data/hooks/useMaintenanceComponentOptions';
import { DueAtRangeType } from '@/features/maintenance-data/hooks/useMaintenanceDataQueryParams';
import { ComponentType, MaintenanceStatus } from '@/generated/GeneratedApiTypes';

/** Synthetic multiselect id for the "solved by customer" chip (not a real status value). */
const SOLVED_BY_CUSTOMER_ID = 'SOLVED_BY_CUSTOMER';

// Due-in and mileage share WFE's bounds.
const DUE_IN_MIN = -100000;
const DUE_IN_MAX = 1000000;
const DUE_IN_STEP = 1000;
const MILEAGE_MIN = 0;
const MILEAGE_MAX = 1000000;
const MILEAGE_STEP = 1000;

export interface MaintenanceDataFiltersProps {
    status: MaintenanceStatus[];
    onStatusChange: (status: MaintenanceStatus[]) => void;
    solvedByCustomer: boolean;
    onSolvedByCustomerChange: (value: boolean) => void;

    componentType: ComponentType[];
    onComponentTypeChange: (types: ComponentType[]) => void;

    component: string[];
    onComponentChange: (components: string[]) => void;

    dueDateFrom?: string;
    dueDateTo?: string;
    dueAtRangeType?: DueAtRangeType;
    onDueAtChange: (change: DueAtChange) => void;

    dueMileageMin?: number;
    dueMileageMax?: number;
    onDueInChange: (range: RangeValue) => void;

    mileageMin?: number;
    mileageMax?: number;
    onMileageChange: (range: RangeValue) => void;

    dueRangesOrGate: boolean;
    onDueRangesOrGateChange: (value: boolean) => void;

    isLoading: boolean;
}

// ─── Static option sets (stable references, no per-render allocation) ────────

const STATUS_OPTIONS: MultiselectOption[] = [
    {
        id: MaintenanceStatus.OVERDUE,
        label: <FormattedMessage id={`${FILTER_MSG}.status.overdue`} />,
        icon: <span className="rioglyph rioglyph-error-sign text-color-danger text-size-18" />,
    },
    {
        id: MaintenanceStatus.DUE,
        label: <FormattedMessage id={`${FILTER_MSG}.status.almostDue`} />,
        icon: <span className="rioglyph rioglyph-warning-sign text-color-warning text-size-18" />,
    },
    {
        id: MaintenanceStatus.NEXT,
        label: <FormattedMessage id={`${FILTER_MSG}.status.next`} />,
        icon: <span className="rioglyph rioglyph-ok-sign text-color-success text-size-18" />,
    },
    {
        id: MaintenanceStatus.UNKNOWN,
        label: <FormattedMessage id={`${FILTER_MSG}.status.unknown`} />,
        icon: <span className="rioglyph rioglyph-question-sign text-color-gray text-size-18" />,
    },
    {
        id: SOLVED_BY_CUSTOMER_ID,
        label: <FormattedMessage id={`${FILTER_MSG}.status.solvedByCustomer`} />,
        icon: <span className="rioglyph rioglyph-series text-color-info text-size-18" />,
    },
];

const COMPONENT_TYPE_OPTIONS: MultiselectOption[] = [
    {
        id: ComponentType.MAINTENANCE_COMPONENT,
        label: <FormattedMessage id={`${FILTER_MSG}.component-type.vehicle`} />,
        icon: <span className="rioglyph rioglyph-component gray text-size-18" />,
    },
    {
        id: ComponentType.CUSTOM_COMPONENT,
        label: <FormattedMessage id={`${FILTER_MSG}.component-type.custom`} />,
        icon: <span className="rioglyph rioglyph-component-custom gray text-size-18" />,
    },
];

// ─── Component ───────────────────────────────────────────────────────────────

const MaintenanceDataFilters: React.FC<MaintenanceDataFiltersProps> = ({
    status,
    onStatusChange,
    solvedByCustomer,
    onSolvedByCustomerChange,
    componentType,
    onComponentTypeChange,
    component,
    onComponentChange,
    dueDateFrom,
    dueDateTo,
    dueAtRangeType,
    onDueAtChange,
    dueMileageMin,
    dueMileageMax,
    onDueInChange,
    mileageMin,
    mileageMax,
    onMileageChange,
    dueRangesOrGate,
    onDueRangesOrGateChange,
    isLoading,
}) => {
    const intl = useIntl();
    const maintenanceComponents = useMaintenanceComponentOptions();

    // The status control carries the four real statuses plus the synthetic "solved by customer"
    // chip. On change we split them back into the status array and the dedicated boolean.
    const handleStatusSelect = (selectedIds: string[]) => {
        onSolvedByCustomerChange(selectedIds.includes(SOLVED_BY_CUSTOMER_ID));
        onStatusChange(selectedIds.filter((id) => id !== SOLVED_BY_CUSTOMER_ID) as MaintenanceStatus[]);
    };

    // Component filter is only meaningful for vehicle (maintenance) components — mirrors WFE's gating.
    const isComponentFilterEnabled =
        componentType.length === 0 || componentType.includes(ComponentType.MAINTENANCE_COMPONENT);

    const componentFilterOptions: MultiselectOption[] = useMemo(
        () => maintenanceComponents.map(({ slug, label }) => ({ id: slug, label })),
        [maintenanceComponents],
    );

    const statusValue = solvedByCustomer ? [...status, SOLVED_BY_CUSTOMER_ID] : [...status];

    return (
        <div className="maintenance-data-filters panel panel-default padding-20 text-size-12 margin-top-20">
            <div className="display-grid grid-cols-3-sm gap-20 height-70">
                <MaintenanceDueAtFilter
                    dueDateFrom={dueDateFrom}
                    dueDateTo={dueDateTo}
                    dueAtRangeType={dueAtRangeType}
                    onChange={onDueAtChange}
                    disabled={isLoading}
                />
                <RangeInputDropdown
                    title={`${FILTER_MSG}.dueIn.title`}
                    fromValue={dueMileageMin}
                    toValue={dueMileageMax}
                    min={DUE_IN_MIN}
                    max={DUE_IN_MAX}
                    step={DUE_IN_STEP}
                    onApply={onDueInChange}
                    disabled={isLoading}
                />
                <div className="display-flex align-items-end padding-bottom-25">
                    <Checkbox
                        checked={dueRangesOrGate}
                        onChange={(event) => onDueRangesOrGateChange(event.target.checked)}
                        disabled={isLoading}
                    >
                        <FormattedMessage id={`${FILTER_MSG}.dueRangesOperator`} />
                    </Checkbox>
                </div>
            </div>
            <div className="display-grid grid-cols-3-sm gap-20 height-70">
                <FilterMultiselect
                    title={<FormattedMessage id={`${FILTER_MSG}.status.title`} />}
                    placeholder={intl.formatMessage({ id: `${FILTER_MSG}.placeholder` })}
                    options={STATUS_OPTIONS}
                    value={statusValue}
                    onChange={handleStatusSelect}
                    disabled={isLoading}
                    showSelectedItemIcon
                    showUnselectedItemIcons
                />
                <FilterMultiselect
                    title={<FormattedMessage id={`${FILTER_MSG}.component-type.title`} />}
                    placeholder={intl.formatMessage({ id: `${FILTER_MSG}.placeholder` })}
                    options={COMPONENT_TYPE_OPTIONS}
                    value={componentType}
                    onChange={(ids) => onComponentTypeChange(ids as ComponentType[])}
                    disabled={isLoading}
                    showSelectedItemIcon
                    showUnselectedItemIcons
                />
                <FilterMultiselect
                    title={<FormattedMessage id={`${FILTER_MSG}.component.title`} />}
                    placeholder={intl.formatMessage({ id: `${FILTER_MSG}.placeholder` })}
                    options={componentFilterOptions}
                    value={component}
                    onChange={onComponentChange}
                    disabled={isLoading || !isComponentFilterEnabled}
                />
            </div>
            <div className="display-grid grid-cols-3-sm gap-20 height-80">
                <RangeInputDropdown
                    title={`${FILTER_MSG}.mileage.title`}
                    fromValue={mileageMin}
                    toValue={mileageMax}
                    min={MILEAGE_MIN}
                    max={MILEAGE_MAX}
                    step={MILEAGE_STEP}
                    onApply={onMileageChange}
                    disabled={isLoading}
                />
            </div>
        </div>
    );
};

export default MaintenanceDataFilters;
